// Who in the room is recording right now.
//
// Local recording writes a file on one participant's machine, so nothing on the
// server knows it is happening. The recorder announces it over the room channel
// and everyone else reflects it — otherwise people are recorded with no
// indication at all, which is a consent problem and in several jurisdictions a
// legal one.
//
// Cloud recording is different: the server already knows (it is the one doing
// it), so its start/stop events here are not tracked in the peer map. They
// still poke `onCloudChange` so the caller re-reads the server's own status
// through useCloudRecording, but the announcement itself is also kept below:
// a guest (or anyone whose app sign-in token is missing or expired) cannot
// read that status API at all, so the channel event is the only way they
// ever learn a cloud recording is running.

import { useEffect, useRef, useState } from 'react'
import type { PhoenixEventHandler } from './usePhoenixChannel'

export type RecordingNotice = {
  /** A remote LOCAL recorder, or the local user's own recording. Cloud
   *  recording is not folded in here: the caller tracks that separately. */
  someoneIsRecording: boolean
  /** Display names of remote recorders, for the toast / tooltip. */
  recorderNames: string[]
  /** A cloud recording start/stop was announced over the channel. This is the
   *  channel's own view, so it works even when the status API is unreachable. */
  cloudAnnounced: boolean
  /** Name of whoever announced the running cloud recording, if known. */
  cloudStartedBy: string | null
}

export function useRecordingNotice(
  subscribe: (event: string, handler: PhoenixEventHandler) => () => void,
  // No longer gates the subscription (see below). Kept so call sites do not
  // need to change their argument list.
  ready: boolean,
  localIsRecording: boolean,
  onCloudChange?: () => void,
): RecordingNotice {
  const [remote, setRemote] = useState<Map<string, string>>(new Map())
  const [cloudActive, setCloudActive] = useState(false)
  const [cloudStartedBy, setCloudStartedBy] = useState<string | null>(null)
  const cloudChangeRef = useRef(onCloudChange)
  cloudChangeRef.current = onCloudChange

  useEffect(() => {
    // usePhoenixChannel's subscribe only registers a handler in a local map:
    // it does not require the channel to have joined yet, the same way
    // usePresence subscribes. Gating on `ready` risked missing an announcement
    // that arrived in the brief window right after the channel joined.
    const started = subscribe('media:recording_started', (payload) => {
      const { peerId, name, kind } = payload as { peerId?: string; name?: string; kind?: string }
      if (kind === 'cloud') {
        const startedBy = name ?? null
        setCloudActive((prev) => (prev ? prev : true))
        setCloudStartedBy((prev) => (prev === startedBy ? prev : startedBy))
        cloudChangeRef.current?.()
        return
      }
      if (!peerId) return
      setRemote((prev) => {
        if (prev.get(peerId) === (name || peerId)) return prev
        const next = new Map(prev)
        next.set(peerId, name || peerId)
        return next
      })
    })
    const stopped = subscribe('media:recording_stopped', (payload) => {
      const { peerId, kind } = payload as { peerId?: string; kind?: string }
      if (kind === 'cloud') {
        setCloudActive((prev) => (prev ? false : prev))
        setCloudStartedBy((prev) => (prev === null ? prev : null))
        cloudChangeRef.current?.()
        return
      }
      if (!peerId) return
      setRemote((prev) => {
        if (!prev.has(peerId)) return prev
        const next = new Map(prev)
        next.delete(peerId)
        return next
      })
    })
    // A recorder who drops off the call cannot send the stop event, so their
    // indicator would stick forever. Cloud state lives on the server, not on
    // any one peer, so a peer leaving must never clear it.
    const left = subscribe('media:peer_left', (payload) => {
      const { peerId } = payload as { peerId?: string }
      if (!peerId) return
      setRemote((prev) => {
        if (!prev.has(peerId)) return prev
        const next = new Map(prev)
        next.delete(peerId)
        return next
      })
    })
    return () => { started(); stopped(); left() }
  }, [subscribe])

  return {
    someoneIsRecording: localIsRecording || remote.size > 0,
    recorderNames: [...remote.values()],
    cloudAnnounced: cloudActive,
    cloudStartedBy,
  }
}
